import React, { createContext, useContext, useReducer, ReactNode } from 'react';

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  category: string;
  description: string;
  scent: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  discount?: number;
}

export interface CartItem extends Product {
  quantity: number;
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface AppState {
  products: Product[];
  cart: CartItem[];
  user: User | null;
  searchQuery: string;
  selectedCategory: string;
  isSignedIn: boolean;
}

type AppAction =
  | { type: 'ADD_TO_CART'; payload: Product }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_CATEGORY'; payload: string }
  | { type: 'SIGN_IN'; payload: User }
  | { type: 'SIGN_OUT' };

const initialState: AppState = {
  products: [
    {
      id: '1',
      name: 'AKIDO 1',
      price: 1499,
      originalPrice: 2500,
      image: 'https://images.pexels.com/photos/1667071/pexels-photo-1667071.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Premium signature fragrance with deep masculine notes',
      scent: 'Woody, Amber, Musk',
      rating: 4.8,
      reviews: 324,
      inStock: true,
      discount: 40,
    },
    {
      id: '2',
      name: 'AKIDO 2',
      price: 699,
      originalPrice: 1200,
      image: 'https://images.pexels.com/photos/1667068/pexels-photo-1667068.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Fresh and vibrant fragrance for everyday wear',
      scent: 'Citrus, Marine, Cedar',
      rating: 4.6,
      reviews: 198,
      inStock: true,
      discount: 42,
    },
    {
      id: '3',
      name: 'AKIDO 3',
      price: 599,
      originalPrice: 1200,
      image: 'https://images.pexels.com/photos/1667070/pexels-photo-1667070.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Sophisticated blend with oriental undertones',
      scent: 'Spice, Vanilla, Sandalwood',
      rating: 4.7,
      reviews: 156,
      inStock: true,
      discount: 50,
    },
    {
      id: '4',
      name: 'AKIDO 4',
      price: 1199,
      originalPrice: 1999,
      image: 'https://images.pexels.com/photos/1667067/pexels-photo-1667067.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Intense and captivating fragrance for special occasions',
      scent: 'Oud, Rose, Patchouli',
      rating: 4.9,
      reviews: 89,
      inStock: true,
      discount: 40,
    },
    {
      id: '5',
      name: 'AKIDO 5',
      price: 899,
      originalPrice: 1500,
      image: 'https://images.pexels.com/photos/1190829/pexels-photo-1190829.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Modern and dynamic fragrance with fresh appeal',
      scent: 'Bergamot, Lavender, Vetiver',
      rating: 4.5,
      reviews: 203,
      inStock: true,
      discount: 40,
    },
    {
      id: '6',
      name: 'AKIDO 6',
      price: 799,
      originalPrice: 1300,
      image: 'https://images.pexels.com/photos/1667069/pexels-photo-1667069.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Elegant and refined fragrance with timeless appeal',
      scent: 'Jasmine, White Tea, Musk',
      rating: 4.4,
      reviews: 276,
      inStock: true,
      discount: 38,
    },
    {
      id: '7',
      name: 'AKIDO 7',
      price: 1299,
      originalPrice: 2000,
      image: 'https://images.pexels.com/photos/1667071/pexels-photo-1667071.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Bold and adventurous fragrance for the modern man',
      scent: 'Black Pepper, Ginger, Tobacco',
      rating: 4.8,
      reviews: 142,
      inStock: true,
      discount: 35,
    },
    {
      id: '8',
      name: 'AKIDO 8',
      price: 999,
      originalPrice: 1600,
      image: 'https://images.pexels.com/photos/1667068/pexels-photo-1667068.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'signature',
      description: 'Luxurious and sophisticated fragrance with premium ingredients',
      scent: 'Saffron, Leather, Amber',
      rating: 4.7,
      reviews: 189,
      inStock: true,
      discount: 37,
    },
  ],
  cart: [],
  user: null,
  searchQuery: '',
  selectedCategory: 'all',
  isSignedIn: false,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.cart.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          cart: state.cart.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        };
      }
      return {
        ...state,
        cart: [...state.cart, { ...action.payload, quantity: 1 }],
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        cart: state.cart.filter(item => item.id !== action.payload),
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        cart: state.cart.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };

    case 'CLEAR_CART':
      return {
        ...state,
        cart: [],
      };

    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload,
      };

    case 'SET_CATEGORY':
      return {
        ...state,
        selectedCategory: action.payload,
      };

    case 'SIGN_IN':
      return {
        ...state,
        user: action.payload,
        isSignedIn: true,
      };

    case 'SIGN_OUT':
      return {
        ...state,
        user: null,
        isSignedIn: false,
      };

    default:
      return state;
  }
}

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}